import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/projects', label: 'Running Projects' },
    { to: '/blog', label: 'Blog & News' },
    { to: '/contact', label: 'Contact Us' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/80 border-b border-border/40 shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <div className="relative h-36 w-36 -my-8 mr-3">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/invictus-green/uploads/974218eb-5249-49d9-9fa5-bef235aac409.png`}
                alt="RESCO Invictus Green Energy Solutions"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className={`text-base font-bold tracking-wide ${scrolled ? 'text-primary' : 'text-primary'}`}>INVICTUS GREEN</span>
              <span className={`text-xs tracking-[0.15em] uppercase ${scrolled ? 'text-text_muted' : 'text-text_muted'}`}>Energy Solutions LLP</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  location.pathname === link.to 
                    ? 'text-accent' 
                    : 'text-text_main'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin/login"
              className="ml-4 px-5 py-2 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all hover:-translate-y-0.5"
              data-testid="admin-login-button"
            >
              Admin
            </Link>
          </nav>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-primary"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="lg:hidden bg-white border-t border-border"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col space-y-4 p-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-base font-medium transition-colors ${
                  location.pathname === link.to ? 'text-primary' : 'text-text_main'
                }`}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setMobileMenuOpen(false)}
              className="px-5 py-2 bg-accent text-white rounded font-semibold text-center"
              data-testid="mobile-admin-login-button"
            >
              Admin Login
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
